"use client";
import Footer from "@/components/ui/footer";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { Flip, ScrollTrigger, SplitText } from "gsap/all";
import Image from "next/image";
import React, { useRef, useState } from "react";

gsap.registerPlugin(ScrollTrigger, Flip, SplitText);

const Page = () => {
  const containerRef = useRef(null);
  const horizontalRef = useRef<HTMLDivElement | null>(null);
  useGSAP(
    () => {
      document.fonts.ready.then(() => {
        const splitText = new SplitText(".text-2", { type: "lines,chars,words", linesClass: "line" });
        const splitTitles = new SplitText(".hero-title", { type: "chars" });
        const splitSub = new SplitText(".hero-subtitle", { type: "words" });
        const splitH1 = new SplitText(".h1", { type: "chars, line, lines,words", autoSplit: true, mask: "words", linesClass: "line" });
        const split = new SplitText([".child-1", ".child-2"], {
          type: "lines, words, chars",
          mask: "words"
        });
        // Text fixed pinned
        const textFixed = gsap.timeline({
          scrollTrigger: {
            trigger: ".text-fixed",
            start: "top center",
            end: "+400% top",
            scrub: true,
            // markers: true,
            pin: true,
            pinSpacing: false
          }
        });

        textFixed
          .to(".text-fixed, .child-1, .child-2", {
            zIndex: 10,
            opacity: 1,
            duration: 1
          })
          .from(
            split.chars,
            {
              yPercent: 100,
              opacity: 1,
              duration: 1.4,
              stagger: 0.05,
              ease: "power4.inOut"
            },
            "<"
          )
          .to(
            [".child-1"],
            {
              x: -100,
              opacity: 0,
              duration: 3.4,
              ease: "power2.out"
            },
            "+=2"
          )
          .to(
            [".child-2"],
            {
              x: 100,
              opacity: 0,
              duration: 3.4,
              ease: "power2.out"
            },
            "<"
          )
          .to(".text-fixed", {
            opacity: 0
          });
        const listSplit = SplitText.create(".list-text h3", {
          autoSplit: true,
          type: "lines, words, chars",
          mask: "lines",
          deepSlice: true
        });
        const spans = SplitText.create(".list-text span", {
          autoSplit: true,
          type: "lines, words, chars, elements",
          mask: "chars",
          deepSlice: true
        });

        gsap.set(".list-text", {
          perspective: 600,
          transformStyle: "preserve-3d"
        });

        listText
          .to(
            ".list-text",
            {
              opacity: 1,
              duration: 0.2
            },
            "+5.5"
          )

          .fromTo(
            ".list-text",
            {
              scale: 0.8
            },
            {
              scale: 1,
              duration: 2.2
            },
            "<"
          )
          .from(
            listSplit.chars,
            {
              rotationX: 90, // ganti rotate3d → rotationX

              yPercent: 70,
              opacity: 0,
              stagger: 0.09,
              duration: 2
            },

            "<"
          )

          .from(
            spans.elements,
            {
              xPercent: 150,
              autoAlpha: 0,
              rotate: 0,
              scale: 0.8,
              stagger: 0.04,
              duration: 1
            },
            "<"
          );
        gsap.from(splitText.chars, {
          xPercent: -120,
          opacity: 0,
          stagger: 0.05,
          duration: 1.2,
          ease: "expo.out",
          scrollTrigger: {
            trigger: ".text-2",
            // markers: true,
            scrub: true,
            start: "top 80%",
            invalidateOnRefresh: true,
            end: "bottom center"
          }
        });
        gsap.from(splitH1.chars, {
          yPercent: -120,
          opacity: 0,
          stagger: 0.05,
          duration: 1.2,
          ease: "expo.out",
          scrollTrigger: {
            trigger: ".h1",
            // markers: true,
            scrub: true,
            invalidateOnRefresh: true,
            start: "top 80%",
            end: "bottom center"
          }
        });
        gsap.from(splitTitles.chars, {
          yPercent: 120,
          opacity: 0,
          stagger: 0.05,
          duration: 1.2,
          ease: "expo.out",
          scrollTrigger: {
            invalidateOnRefresh: true,
            trigger: ".hero-title",
            start: "top 80%"
          }
        });

        gsap.from(splitSub.words, {
          yPercent: 100,
          opacity: 0,
          stagger: 0.08,
          duration: 1,
          ease: "back.out(2)",
          scrollTrigger: {
            trigger: ".hero-subtitle",
            start: "top 85%"
          }
        });
      });
      const gallery = document.querySelector(".gallery")!;

      ScrollTrigger.create({
        trigger: ".gallery",
        start: "top center",
        // markers: true,
        onEnter: () => {
          const state = Flip.getState(".gallery-item, .section-3, .gallery", { props: "backgroundColor" });
          gallery.classList.toggle("grid-cols-4");

          gallery.classList.add("grid-cols-2");

          Flip.from(state, {
            duration: 1.2,
            scale: true,
            fade: true,
            onComplete: () => {
              ScrollTrigger.refresh();
            },
            ease: "expo.inOut",
            stagger: 0.05
          });
        },
        onLeaveBack: () => {
          const state = Flip.getState(".gallery-item, .section-3,.gallery", { props: "backgroundColor" });
          gallery.classList.toggle("grid-cols-4");
          gallery.classList.add("grid-cols-2");
          gsap.to(".gallery-item", { opacity: 1 });
          gsap.to(".gallery-item", {
            opacity: 1,
            scale: 1
            // filter: "brightness(0.9)" // 💡 brightness 100% (normal)
          });
          Flip.from(state, {
            duration: 1.2,
            ease: "expo.inOut",
            stagger: 0.05,
            onComplete: () => {
              ScrollTrigger.refresh();
            }
          });
        }
      });

      gsap.timeline({
        scrollTrigger: {
          trigger: ".section3-bgimg",
          start: "top top",
          end: "bottom top",
          scrub: true,
          pin: true,
          pinSpacing: false
        }
      });

      // Wrap image pinned
      const wrapImg = gsap.timeline({
        scrollTrigger: {
          trigger: ".wrap-img",
          start: "top top",
          // endTrigger: ".section-4",
          // ^^^ artinya: mulai di tengah progress textFixed (saat child-1 mulai x:-100)
          end: "+300% top",
          scrub: 0.1,
          pin: true,
          pinSpacing: false
          // markers: true
        }
      });

      wrapImg
        .set(".wrap-img", { zIndex: 9, opacity: 1 }) // pastikan layer di bawah text-fixed

        .from(".img-1, .img-3", { yPercent: -110, ease: "power1.in", duration: 15 })
        .addLabel("start")
        .to(
          containerRef.current,
          {
            backgroundColor: "#171717",
            ease: "power2.in",
            duration: 2
          },
          ">-6.5"
        )
        .from(".img-2", { yPercent: 110, ease: "power1.in", duration: 15 }, "-1")
        .addLabel("sync") // ⬅️ bikin titik waktu bernama "sync"
        .to(
          containerRef.current,
          {
            backgroundColor: "#e8efe4",
            ease: "power2.in",
            duration: 2
          },
          ">"
        )
        .to(".img-1", { xPercent: -100, duration: 5 }, "sync") // mulai bareng
        .to(".img-3", { xPercent: 100, duration: 5 }, "sync")
        .to(".img-2 >  :not(:first-child) ", { y: "130vw", duration: 5, ease: "power1.in" }, "sync") // mulai bareng
        .to(".img-2 > :first-child", { y: "-110vw", duration: 5, ease: "power1.in" }, "sync"); // mulai bareng

      const listText = gsap.timeline({
        scrollTrigger: {
          trigger: ".section-4",
          pin: true,
          start: "top top",
          end: "+300% top",
          scrub: true,
          // markers: true,
          pinSpacing: true
        }
      });
      // Horizontal scroll (section-5)
      if (horizontalRef.current) {
        gsap.to(horizontalRef.current, {
          xPercent: -100,
          ease: "none",
          scrollTrigger: {
            trigger: ".section-5",
            start: "top top",
            end: () => "+=" + horizontalRef.current!.scrollWidth * 2,
            scrub: true,
            pin: true,
            pinSpacing: true,
            invalidateOnRefresh: true
          }
        });
      }
    },
    { scope: containerRef }
  );
  return (
    <div ref={containerRef} className='bg-beige-100 size-full flex relative flex-col'>
      <section className='section-1 relative bg-beige-100 text-beige-900 flex overflow-hidden'>
        {/* === KONTEN KIRI === */}
        <div className='flex-1 min-h-screen flex flex-col justify-between text-center px-8 py-16'>
          {/* HERO TITLE */}
          <div>
            <h2 className='md:text-[9rem] text-[40vw] tracking-widest leading-none text-beige-300 font-bold font-moon-walk hero-title md:tracking-tight'>EXAM</h2>
            <h1 className='font-bebas-neue -mt-16 text-[2rem] tracking-[1em] hero-subtitle'>STUDIO</h1>
          </div>

          {/* GRID UTAMA */}
          <div className='flex-1  flex flex-col md:justify-between  '>
            {/* TEKS ATAS */}
            <div className='md:grid md:grid-cols-2   h-full flex-row flex w-full   md:h-full   '>
              {/* <div className='border-r border-beige-900 flex items-center justify-start px-8'></div> */}
              <div className='flex items-end pb-10 justify-end px-2 text-justify    '>
                <h1 className='md:text-[10rem] text-3xl md:bg-beige-100 text-neutral-900/90  font-extralight   md:rounded-2xl px-2 py-2 md:leading-35 font-raleway uppercase max-w-full'>
                  Shot <br /> Your Story
                </h1>
              </div>
            </div>

            {/* TEKS BAWAH */}
            <div className='grid grid-cols-2  items-end gap-2 text-3xl uppercase text-beige-900 '>
              <div className='flex flex-col border-r border-beige-900 p-8 justify-start items-start'>
                <h1 className='font-bebas-neue text-xl text-start md:text-5xl'>Find Inner Yourself</h1>
                <p className='md:text-base max-w-[270px] mt-2 text-start text-xs lowercase'>Discover the art of expressing your style through modern lens.</p>
              </div>
              <div className='flex flex-col p-8 justify-end items-end text-right'>
                <h1 className='font-bebas-neue md:text-5xl text-xl tracking-tighter '>Capture Your Essence</h1>
                <p className='md:text-base  max-w-[240px] mt-2 ml-auto text-xs lowercase'>Every frame tells your story — powerful, bold, and authentic.</p>
              </div>
            </div>
          </div>
        </div>

        {/* === KONTEN KANAN (VERTIKAL) === */}
        <div className='md:visible hidden md:grid grid-rows-2 place-items-center px-6 w-[2vw] text-nowrap whitespace-nowrap tracking-tight leading-0'>
          <h1 className='-rotate-90 origin-left font-bebas-neue text-xl tracking-widest '>NEW PROJECT</h1>
          <h1 className='rotate-90 origin-left font-bebas-neue text-xl tracking-widest'>UPCOMING SHOTS</h1>
        </div>
      </section>

      <section className=' bg-beige-100 pb-20 section-2 flex pt-50 flex-col px-5  overflow-hidden '>
        <div className=' flex-row flex mb-30 '>
          <h1 className='w-full font-extralight h1  2xl:text-[15rem] tracking-tighter  text-gray-900  text-7xl inline-block'>
            <div className='text-5xl border-2 h-fit text-2  2xl:text-[15rem]  border-zinc-700 font-bold mb-6 bg-neutral-900 p-2   text-beige-100 font-bebas-neue  inline-block'>The Art of Style</div>Every frame, every pose, every shade tells
            a story. Discover how fashion transforms emotion into timeless imagery.
          </h1>
        </div>
        <div className='my-20 bg-neutral-900 md:p-20 p-5 rounded-xl w-full gallery grid grid-cols-4 gap-4 '>
          {["One", "Two", "Three", "Four"].map((v, i) => (
            <div key={i} className='gallery-item aspect-[4/2] bg-accent place-center text-[5vw] font-surgena brightness-80 bg-cover bg-center rounded-2xl  '>
              {v}
            </div>
          ))}
        </div>
      </section>
      <section className='min-h-screen flex  flex-col gap-2   w-full relative section-3 p-5 '>
        <div
          className={`flex flex-col bg-no-repeat section3-bgimg   bg-center bg-cover  size-full bg-[url('/image/wiko_studio/image8.jpg')] md:bg-[url(
          "/image/wiko_studio/image3.jpg")] brightness-50 min-h-[80vh] justify-between items-start rounded-sm  p-6 relative `}
        >
          <div className='flex  place-center gap-2'>
            <span className='bg-neutral-800 size-3 rounded-full'></span> <h1 className='text-3xl text-zinc-900 font-bebas-neue'>Exclucive</h1>
          </div>
          <div className=' place-center md:text-9xl text-2xl font-serif relative text-beige-100'>For Those who come After</div>
        </div>
        <div className='bg-beige-100 relative    pin-div text-9xl'>
          <div className=' flex justify-center'>
            <div className='text-fixed text-2xl uppercase md:text-9xl flex min-h-[30vh]   justify-between px-20 md:px-50 text-beige-900 w-full font-serif  tracking-tighter'>
              <div className='flex child-1 opacity-0 '>Family</div>
              <div className='flex child-2 opacity-0 text-beige-300 '>Frame</div>
            </div>
          </div>

          <div className='pt-30 wrap-img opacity-0 overflow-hidden max-h-svh  text-xs  min-h-screen grid grid-cols-3'>
            <div className='items-center  img-1 flex gap-2  flex-col '>
              {Array.from(["/image/wiko_studio/image1.jpg", "/image/wiko_studio/image2.jpg", "/image/wiko_studio/image3.jpg", "/image/wiko_studio/image1.jpg"]).map((v, i) => (
                <div className='md:h-96 md:w-96 w-24 h-36 flex overflow-hidden bg-white my-10 md:mt-50' id={`item-${i}`} key={i}>
                  <Image src={v || "/placeholder.svg"} width={20} height={20} alt='startup template' sizes='50%' className=' h-auto w-full  rounded-lg object-cover shadow-md object-center ' />
                </div>
              ))}
            </div>
            <div className='items-center img-2 flex gap-2  flex-col '>
              {Array.from(["/image/wiko_studio/image4.jpg", "/image/wiko_studio/image5.jpg", "/image/wiko_studio/image3.jpg", "/image/wiko_studio/image1.jpg"]).map((v, i) => (
                <div className='md:h-96 md:w-96 w-24 h-36 flex overflow-hidden bg-white my-10 md:mt-50' id={`items-${i}`} key={i}>
                  <Image src={v || "/placeholder.svg"} alt='startup template' sizes='100%' fill className=' h-auto w-full  rounded-lg object-cover shadow-md ' />
                </div>
              ))}
            </div>
            <div className='items-center img-3 flex gap-2  flex-col '>
              {Array.from(["/image/wiko_studio/image1.jpg", "/image/wiko_studio/image2.jpg", "/image/wiko_studio/image3.jpg", "/image/wiko_studio/image1.jpg"]).map((v, i) => (
                <div className='md:h-96 md:w-96 w-24 h-36 flex overflow-hidden bg-white my-10 md:mt-50' id={`item-${i}`} key={i}>
                  <Image src={v || "/placeholder.svg"} width={20} height={20} alt='startup template' sizes='50%' className=' h-auto w-full  rounded-lg object-cover shadow-md object-center ' />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id='gallery' className='min-h-screen size-full  flex items-center overflow-hidden  font-serif  justify-center  relative section-4 p-2 '>
        <div className=' mx-auto container list-text opacity-0  flex w-full text-beige-900'>
          <ul className='flex flex-col md:grid md:grid-cols-2 md:grid-rows-3 gap-2  size-full'>
            <li className='place-center gap-2 text-2xl  flex py-2 md:py-10 capitalize justify-between md:px-20 '>
              <h3 className='text-2xl md:text-5xl flex pl-4'>Best Website</h3>
              <div className='flex gap-2 text-xs max-w-40 w-sm flex-wrap  '>
                {["sod 2x", "hm x20", "dod 2x", "me 2x"].map((v, i) => (
                  <span key={i} className='border bg-beige-900 text-beige-100 rounded-sm px-2 py-2 place-center '>
                    {v}
                  </span>
                ))}
              </div>
            </li>
            <li className='place-center py-2 gap-2 text-2xl  flex pt-2 md:py-10 capitalize justify-between md:px-20 '>
              <h3 className='text-2xl md:text-5xl flex pl-4'>Best Website</h3>
              <div className='flex gap-2 text-xs max-w-40 w-sm flex-wrap  '>
                {["sod 2x", "hm x20", "dod 2x", "me 2x"].map((v, i) => (
                  <span key={i} className='border bg-beige-900 text-beige-100 rounded-sm px-2 py-2 place-center '>
                    {v}
                  </span>
                ))}
              </div>
            </li>
            <li className='place-center py-2 gap-2 text-2xl  flex pt-2 md:py-10 capitalize justify-between md:px-20 '>
              <h3 className='text-2xl md:text-5xl flex pl-4'>Best Website</h3>
              <div className='flex gap-2 text-xs max-w-40 w-sm flex-wrap  '>
                {["sod 2x", "hm x20", "dod 2x", "me 2x"].map((v, i) => (
                  <span key={i} className='border bg-beige-900 text-beige-100 rounded-sm px-2 py-2 place-center '>
                    {v}
                  </span>
                ))}
              </div>
            </li>
            <li className='place-center py-2 gap-2 text-2xl  flex pt-2 md:py-10 capitalize justify-between md:px-20 '>
              <h3 className='text-2xl md:text-5xl flex pl-4'>Best Website</h3>
              <div className='flex gap-2 text-xs max-w-40 w-sm flex-wrap  '>
                {["sod 2x", "hm x20", "dod 2x", "me 2x"].map((v, i) => (
                  <span key={i} className='border bg-beige-900 text-beige-100 rounded-sm px-2 py-2 place-center '>
                    {v}
                  </span>
                ))}
              </div>
            </li>
            <li className='place-center py-2 gap-2 text-2xl  flex pt-2 md:py-10 capitalize justify-between md:px-20 '>
              <h3 className='text-2xl md:text-5xl flex pl-4'>Best Website</h3>
              <div className='flex gap-2 text-xs max-w-40 w-sm flex-wrap  '>
                {["sod 2x", "hm x20", "dod 2x", "me 2x"].map((v, i) => (
                  <span key={i} className='border bg-beige-900 text-beige-100 rounded-sm px-2 py-2 place-center '>
                    {v}
                  </span>
                ))}
              </div>
            </li>
            <li className='place-center py-2 gap-2 text-2xl  flex pt-2 md:py-10 capitalize justify-between md:px-20 '>
              <h3 className='text-2xl md:text-5xl flex pl-4'>Best Website</h3>
              <div className='flex gap-2 text-xs max-w-40 w-sm flex-wrap  '>
                {["sod 2x", "hm x20", "dod 2x", "me 2x"].map((v, i) => (
                  <span key={i} className='border bg-beige-900 text-beige-100 rounded-sm px-2 py-2 place-center '>
                    {v}
                  </span>
                ))}
              </div>
            </li>
          </ul>
        </div>
      </section>
      <section className='  relative section-5 size-full overflow-hidden bg-beige-900 py-20 w-full flex px-6'>
        <div className='flex h-full w-full md:flex-row flex-col'>
          <div className='flex  md:bg-beige-300 rounded-2xl text-neutral-800 px-2 w-full justify-around mr-[30vw]  z-50 backdrop-blur-3xl items-center drop-shadow-2xl backdrop:shadow-2xl shadow-2xl'>
            <div className=' text-7xl font-bold font-raleway   md:text-9xl 2xl:text-[10vw]   max-w-2xl md:max-w-full mb-10'>STUDIO GALLERY</div>
          </div>
          <div ref={horizontalRef} className='extra-long-container  invert-100  flex gap-10 w-fit'>
            {["/image/wiko_studio/wedding.webp", "/image/wiko_studio/weddign1.webp", "/image/wiko_studio/wedding2.webp", "/image/wiko_studio/wedding3.webp"].map((src, i) => (
              <div key={i} className='gallery-items flex flex-col-reverse  items-center'>
                <h1 className='text-xs pt-3 px-5'>PREWEDDING {i + 1}</h1>
                <div className='md:w-96 w-60 h-72  md:h-[40rem] relative overflow-hidden rounded-2xl'>
                  <Image src={src} alt='wedding' fill sizes='100%' style={{ objectFit: "cover" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer id={"contact"} />
    </div>
  );
};

export default Page;
